import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, object = signed in, false = signed out
  const [user, setUser] = useState(null);
  const [saved, setSaved] = useState([]);
  const [prompt, setPrompt] = useState(null); // { reason, tab } when the sign-in dialog is open
  const afterAuth = useRef(null);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      try {
        const { data } = await api.post("/auth/refresh");
        setUser(data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  useEffect(() => {
    if (!user) {
      setSaved([]);
      return;
    }
    api.get("/me/saved").then((r) => setSaved(Array.isArray(r.data?.keys) ? r.data.keys : [])).catch(() => setSaved([]));
  }, [user]);

  const finish = (data) => {
    setUser(data);
    setPrompt(null);
    const cb = afterAuth.current;
    afterAuth.current = null;
    cb?.(data);
    return data;
  };

  const login = async (email, password) => finish((await api.post("/auth/login", { email, password })).data);
  const register = async (form) => finish((await api.post("/auth/register", form)).data);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(false);
  };

  /** Open the sign-in / sign-up dialog; `then` runs after a successful sign-in. */
  const requireAccount = useCallback((reason, then, tab = "register") => {
    afterAuth.current = then || null;
    setPrompt({ reason, tab });
  }, []);
  const closePrompt = () => {
    afterAuth.current = null;
    setPrompt(null);
  };

  /** Save / unsave a home. Returns true (saved), false (removed) or null (needs an account). */
  const toggleSaved = async (key) => {
    if (!user) {
      requireAccount("Create a free account to save homes and get back to them on any device.", async () => {
        const r = await api.put(`/me/saved/${key}`);
        setSaved(r.data?.keys ?? []);
      });
      return null;
    }
    const on = saved.includes(key);
    const r = on ? await api.delete(`/me/saved/${key}`) : await api.put(`/me/saved/${key}`);
    setSaved(r.data?.keys ?? []);
    return !on;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, check, saved, toggleSaved, prompt, requireAccount, closePrompt }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
